import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import type { DailyProduction } from "@shared/types";

// Colour palette for stacked areas
const COLORS = [
  "hsl(220, 70%, 50%)",
  "hsl(142, 71%, 45%)",
  "hsl(45, 93%, 47%)",
  "hsl(280, 60%, 50%)",
  "hsl(0, 84%, 60%)",
  "hsl(190, 80%, 40%)",
  "hsl(30, 80%, 50%)",
  "hsl(160, 60%, 45%)",
];

interface ProductionTrendProps {
  data: DailyProduction[];
}

export function ProductionTrend({ data }: ProductionTrendProps) {
  if (data.length === 0) {
    return (
      <div className="flex h-80 items-center justify-center text-muted-foreground">
        No production data for selected period
      </div>
    );
  }

  // Pivot data: group by date, with one key per part
  const parts = [...new Set(data.map((d) => d.part_name))];
  const byDate = new Map<string, Record<string, unknown>>();

  for (const d of data) {
    const existing = byDate.get(d.date) || ({ date: d.date } as Record<string, unknown>);
    existing[d.part_name] = (Number(existing[d.part_name]) || 0) + d.total_quantity;
    byDate.set(d.date, existing);
  }

  const chartData = [...byDate.values()].sort((a, b) =>
    String(a.date).localeCompare(String(b.date))
  );

  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            tickFormatter={(d) => {
              const date = new Date(d);
              return `${date.getDate()}/${date.getMonth() + 1}`;
            }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            label={{
              value: "Parts",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 11 },
            }}
          />

          {parts.map((product, i) => (
            <Area
              key={product}
              type="monotone"
              dataKey={product}
              stackId="1"
              fill={COLORS[i % COLORS.length]}
              stroke={COLORS[i % COLORS.length]}
              fillOpacity={0.4}
            />
          ))}

          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const total = payload.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
              return (
                <div className="rounded-lg border border-border bg-popover p-3 text-sm text-popover-foreground shadow-md max-h-60 overflow-y-auto">
                  <p className="font-medium mb-1">{label}</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    Total: {total.toLocaleString()}
                  </p>
                  {payload.map((p) => (
                    <div key={p.dataKey as string} className="flex items-center gap-2 text-xs">
                      <div
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: p.color }}
                      />
                      <span className="min-w-0 truncate">{p.dataKey as string}</span>
                      <span className="ml-auto font-medium">
                        {Number(p.value).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: 11 }}
            formatter={(value) => <span className="text-muted-foreground">{value}</span>}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
